import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import {
  useDeleteAttendance,
  useUpdateAttendance,
} from "@/hooks/api/useAttendances"
import type {
  AttendanceResponse,
  AttendanceUpdateInput,
} from "@/types/attendance"
import { attendanceUpdateSchema } from "@/types/attendance"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Controller,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form"

interface AttendanceActionsMenuProps {
  attendance: AttendanceResponse
}

export const AttendanceActionsMenu = memo(
  ({ attendance }: AttendanceActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const deleteAttendanceMutation = useDeleteAttendance()
    const updateAttendanceMutation = useUpdateAttendance()

    const form = useForm<AttendanceUpdateInput>({
      resolver: zodResolver(
        attendanceUpdateSchema,
      ) as Resolver<AttendanceUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        enrollment_id: attendance.enrollment_id ?? "",
        class_session_id: attendance.class_session_id ?? "",
        student_id: attendance.student_id ?? "",
        class_date: attendance.class_date ?? "",
        class_time: attendance.class_time ?? "",
        status: attendance.status ?? null,
        check_in_time: attendance.check_in_time ?? "",
        check_out_time: attendance.check_out_time ?? "",
        is_makeup_class: attendance.is_makeup_class ?? false,
        makeup_for_date: attendance.makeup_for_date ?? "",
        instructor_notes: attendance.instructor_notes ?? "",
        skills_practiced: attendance.skills_practiced
          ? JSON.stringify(attendance.skills_practiced)
          : "",
        behavior_notes: attendance.behavior_notes ?? "",
        parent_notification_sent: attendance.parent_notification_sent ?? false,
        marked_by_user_id: attendance.marked_by_user_id ?? "",
        notes: attendance.notes ?? "",
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(attendance.enrollment_id ?? attendance.id)

    const handleDelete = async () => {
      try {
        await deleteAttendanceMutation.mutateAsync(attendance.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "Attendance deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting attendance:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete attendance"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to delete this attendance"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<AttendanceUpdateInput> = async (data) => {
      try {
        await updateAttendanceMutation.mutateAsync({
          attendanceId: attendance.id,
          attendanceData: data,
        })
        setShowEditDialog(false)
        form.reset({
          enrollment_id: attendance.enrollment_id ?? "",
          class_session_id: attendance.class_session_id ?? "",
          student_id: attendance.student_id ?? "",
          class_date: attendance.class_date ?? "",
          class_time: attendance.class_time ?? "",
          status: attendance.status ?? null,
          check_in_time: attendance.check_in_time ?? "",
          check_out_time: attendance.check_out_time ?? "",
          is_makeup_class: attendance.is_makeup_class ?? false,
          makeup_for_date: attendance.makeup_for_date ?? "",
          instructor_notes: attendance.instructor_notes ?? "",
          skills_practiced: attendance.skills_practiced
            ? JSON.stringify(attendance.skills_practiced)
            : "",
          behavior_notes: attendance.behavior_notes ?? "",
          parent_notification_sent:
            attendance.parent_notification_sent ?? false,
          marked_by_user_id: attendance.marked_by_user_id ?? "",
          notes: attendance.notes ?? "",
        })
        toaster.create({
          title: "Attendance updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating attendance:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update attendance"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this attendance"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        enrollment_id: attendance.enrollment_id ?? "",
        class_session_id: attendance.class_session_id ?? "",
        student_id: attendance.student_id ?? "",
        class_date: attendance.class_date ?? "",
        class_time: attendance.class_time ?? "",
        status: attendance.status ?? null,
        check_in_time: attendance.check_in_time ?? "",
        check_out_time: attendance.check_out_time ?? "",
        is_makeup_class: attendance.is_makeup_class ?? false,
        makeup_for_date: attendance.makeup_for_date ?? "",
        instructor_notes: attendance.instructor_notes ?? "",
        skills_practiced: attendance.skills_practiced
          ? JSON.stringify(attendance.skills_practiced)
          : "",
        behavior_notes: attendance.behavior_notes ?? "",
        parent_notification_sent: attendance.parent_notification_sent ?? false,
        marked_by_user_id: attendance.marked_by_user_id ?? "",
        notes: attendance.notes ?? "",
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Attendance</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.enrollment_id}
                  errorText={getErrorText(form.formState.errors.enrollment_id)}
                  label="Enrollment Id"
                >
                  <Input
                    placeholder={"Enter enrollment id"}
                    aria-invalid={!!form.formState.errors.enrollment_id}
                    {...form.register("enrollment_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.class_session_id}
                  errorText={getErrorText(
                    form.formState.errors.class_session_id,
                  )}
                  label="Class Session Id"
                >
                  <Input
                    placeholder={"Enter class session id"}
                    aria-invalid={!!form.formState.errors.class_session_id}
                    {...form.register("class_session_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.student_id}
                  errorText={getErrorText(form.formState.errors.student_id)}
                  label="Student Id"
                >
                  <Input
                    placeholder={"Enter student id"}
                    aria-invalid={!!form.formState.errors.student_id}
                    {...form.register("student_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.class_date}
                  errorText={getErrorText(form.formState.errors.class_date)}
                  label="Class Date"
                >
                  <Input
                    type="date"
                    placeholder={"Select class date"}
                    aria-invalid={!!form.formState.errors.class_date}
                    {...form.register("class_date")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.class_time}
                  errorText={getErrorText(form.formState.errors.class_time)}
                  label="Class Time"
                >
                  <Input
                    placeholder={"Enter class time"}
                    aria-invalid={!!form.formState.errors.class_time}
                    {...form.register("class_time")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.status}
                  errorText={getErrorText(form.formState.errors.status)}
                  label="Status"
                >
                  <select
                    {...form.register("status", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.status}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="">Select Status</option>{" "}
                    <option value="present">Present</option>{" "}
                    <option value="absent">Absent</option>{" "}
                    <option value="excused">Excused</option>{" "}
                    <option value="late">Late</option>{" "}
                    <option value="makeup">Makeup</option>{" "}
                    <option value="cancelled">Cancelled</option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.check_in_time}
                  errorText={getErrorText(form.formState.errors.check_in_time)}
                  label="Check In Time"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter check in time"}
                    aria-invalid={!!form.formState.errors.check_in_time}
                    {...form.register("check_in_time")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.check_out_time}
                  errorText={getErrorText(form.formState.errors.check_out_time)}
                  label="Check Out Time"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter check out time"}
                    aria-invalid={!!form.formState.errors.check_out_time}
                    {...form.register("check_out_time")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_makeup_class}
                  errorText={getErrorText(
                    form.formState.errors.is_makeup_class,
                  )}
                  label="Is Makeup Class"
                >
                  <Controller
                    name="is_makeup_class"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_makeup_class"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Makeup Class"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.makeup_for_date}
                  errorText={getErrorText(
                    form.formState.errors.makeup_for_date,
                  )}
                  label="Makeup For Date"
                >
                  <Input
                    type="date"
                    placeholder={"Select makeup for date"}
                    aria-invalid={!!form.formState.errors.makeup_for_date}
                    {...form.register("makeup_for_date")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.instructor_notes}
                  errorText={getErrorText(
                    form.formState.errors.instructor_notes,
                  )}
                  label="Instructor Notes"
                >
                  <Textarea
                    {...form.register("instructor_notes")}
                    placeholder={"Enter instructor notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.instructor_notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.skills_practiced}
                  errorText={getErrorText(
                    form.formState.errors.skills_practiced,
                  )}
                  label="Skills Practiced"
                >
                  <Input
                    placeholder={"Enter skills practiced"}
                    aria-invalid={!!form.formState.errors.skills_practiced}
                    {...form.register("skills_practiced")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.behavior_notes}
                  errorText={getErrorText(form.formState.errors.behavior_notes)}
                  label="Behavior Notes"
                >
                  <Textarea
                    {...form.register("behavior_notes")}
                    placeholder={"Enter behavior notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.behavior_notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.parent_notification_sent}
                  errorText={getErrorText(
                    form.formState.errors.parent_notification_sent,
                  )}
                  label="Parent Notification Sent"
                >
                  <Controller
                    name="parent_notification_sent"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="parent_notification_sent"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Parent Notification Sent"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.marked_by_user_id}
                  errorText={getErrorText(
                    form.formState.errors.marked_by_user_id,
                  )}
                  label="Marked By User Id"
                >
                  <Input
                    placeholder={"Enter marked by user id"}
                    aria-invalid={!!form.formState.errors.marked_by_user_id}
                    {...form.register("marked_by_user_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateAttendanceMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateAttendanceMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Attendance
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Attendance</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The attendance will be permanently
                removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteAttendanceMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteAttendanceMutation.isPending}
                >
                  {deleteAttendanceMutation.isPending
                    ? "Deleting..."
                    : "Delete Attendance"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

AttendanceActionsMenu.displayName = "AttendanceActionsMenu"

export default AttendanceActionsMenu
